import { Button } from "@/components/ui/button";
import { DownloadSimple } from "@phosphor-icons/react";
import { exportToXlsx, exportStamp } from "@/lib/exportXlsx";
import { toast } from "sonner";

/**
 * Renders a small outline button that exports the supplied rows to .xlsx.
 *
 * Usage:
 *   <ExportButton
 *     filename="dashboard-firms"
 *     columns={[{key:"firm_name",label:"Firm"}, ...]}
 *     rows={firms}
 *   />
 *
 * `filename` is appended with today's date automatically.
 */
export default function ExportButton({
  filename,
  sheetName,
  columns,
  rows,
  fetchRows, // optional async () => rows — used when full dataset must be fetched on click
  label = "Export",
  size = "sm",
  variant = "outline",
  className = "",
  testId,
  disabled = false,
}) {
  const click = async () => {
    let data = rows;
    if (typeof fetchRows === "function") {
      try {
        data = await fetchRows();
      } catch (e) {
        toast.error(`Export failed: ${e?.message || "could not fetch rows"}`);
        return;
      }
    }
    if (!data || data.length === 0) {
      toast.info("Nothing to export — the table is empty.");
      return;
    }
    try {
      exportToXlsx({
        filename: `${filename}-${exportStamp()}`,
        sheetName: sheetName || filename,
        columns,
        rows: data,
      });
      toast.success(`Exported ${data.length} row${data.length === 1 ? "" : "s"}`);
    } catch (e) {
      toast.error(`Export failed: ${e?.message || "unknown error"}`);
    }
  };

  return (
    <Button
      type="button"
      size={size}
      variant={variant}
      onClick={click}
      disabled={disabled}
      className={className}
      data-testid={testId || `export-${filename}`}
    >
      <DownloadSimple size={14} className="mr-1.5" />
      {label}
    </Button>
  );
}
